import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import * as R from 'ramda'
import { BackButton, ButtonWithLoader } from '@pbt/pbt-ui-components'

import DialogNames from '~/constants/DialogNames'
import {
  getBundlesIsCreating,
  getBundlesIsLoading,
} from '~/store/reducers/bundles'
import type { Bundle, BundleItem, Task } from '~/types'
import useDialog from '~/utils/useDialog'

import BundleItemsTable, {
  type BundleItemsTableHandle,
} from './BundleItemsTable'
import BundleTasksTable from './BundleTasksTable'

const useStyles = makeStyles(
  () => ({
    root: {},
    button: {
      minWidth: 196,
      height: 40,
    },
  }),
  { name: 'FinalizeBundle' },
)

export interface FinalizeBundleHandle {
  getCandidate: () => Bundle
}

interface FinalizeBundleProps {
  bundle: Bundle
  isLoading?: boolean
  onBack: () => void
  onProceed: (bundle: Bundle) => void
  onUpdateBundleCandidate: (
    bundle: Bundle,
    shouldFetchDifferenceDistribution?: boolean,
  ) => void
  originalBundle: Bundle
  proceedButtonLabel?: string
}

const FinalizeBundle = forwardRef<FinalizeBundleHandle, FinalizeBundleProps>(
  function FinalizeBundle(
    {
      originalBundle,
      proceedButtonLabel: proceedButtonLabelProp,
      bundle,
      onBack,
      onProceed,
      onUpdateBundleCandidate,
      isLoading: isLoadingProp,
    },
    ref,
  ) {
    const classes = useStyles()
    const isCreating = useSelector(getBundlesIsCreating)
    const isLoading = useSelector(getBundlesIsLoading)
    const { t } = useTranslation('Common')
    const proceedButtonLabel =
      proceedButtonLabelProp || t('Common:CREATE_BUNDLE')

    const bundleItemsTableRef = useRef<BundleItemsTableHandle>(null)

    const [openBundleTaskDialog] = useDialog(DialogNames.BUNDLE_TASK)

    const deleteItem = (item: BundleItem) => {
      onUpdateBundleCandidate(
        {
          ...bundle,
          items: R.without([item], bundle.items || []),
        },
        true,
      )
    }

    const onBundleTaskDialogProceed = (task: Task, index?: number) => {
      const oldTasks = bundle.tasks || []
      const newTasks = R.isNil(index)
        ? oldTasks.concat(task)
        : R.update(index, task, oldTasks)

      onUpdateBundleCandidate(
        {
          ...bundle,
          tasks: newTasks,
        },
        false,
      )
    }

    const onEditTask = (index: number) => {
      const task = bundle?.tasks?.[index]

      openBundleTaskDialog({
        task,
        onProceed: (proceededTask: Task) =>
          onBundleTaskDialogProceed(proceededTask, index),
      })
    }

    const onDeleteTask = (index: number) => {
      onUpdateBundleCandidate(
        {
          ...bundle,
          tasks: R.remove(index, 1, bundle?.tasks || []),
        },
        false,
      )
    }

    const onAddTask = () => {
      openBundleTaskDialog({
        onProceed: onBundleTaskDialogProceed,
      })
    }

    const validate = () => bundleItemsTableRef.current?.validate()

    const tryProceed = () => {
      if (validate()) {
        onProceed(bundle)
      }
    }

    useImperativeHandle(ref, () => ({
      getCandidate: () => bundle,
    }))

    const loading = isLoading || isCreating || isLoadingProp

    return (
      <Grid container item className={classes.root} px={3} py={2}>
        <BundleItemsTable
          bundle={bundle}
          originalBundle={originalBundle}
          ref={bundleItemsTableRef}
          onBundleChange={onUpdateBundleCandidate}
          onDelete={deleteItem}
        />
        <BundleTasksTable
          bundle={bundle}
          onAdd={onAddTask}
          onDelete={onDeleteTask}
          onEdit={onEditTask}
        />
        <Grid container item alignItems="flex-end" columnSpacing={3} mt={2}>
          <Grid item>
            <BackButton label={t('Common:BACK_ACTION')} onClick={onBack} />
          </Grid>
          <Grid item>
            <ButtonWithLoader
              className={classes.button}
              disabled={loading}
              loading={loading}
              onClick={tryProceed}
            >
              {proceedButtonLabel}
            </ButtonWithLoader>
          </Grid>
        </Grid>
      </Grid>
    )
  },
)

export default FinalizeBundle
